import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, startWith, takeUntil, tap } from 'rxjs/operators';

import { EditorDataService } from '../editor-data/editor-data.service';
import {
  EXAM_TYPES_OPTIONS,
  ExamTypeEnum,
} from "@fry/connections/lib/connection";


@Component({
  selector: 'eas-editor-booking-item-tree-exam-type-form',
  templateUrl: './editor-booking-item-tree-exam-type-form.component.html',
  standalone: false,
})
export class EditorBookingItemTreeExamTypeFormComponent implements OnInit, OnDestroy {
  @Input() parentFormGroup: FormGroup;
  @Input() itemTreeExamTypeForm: FormControl;

  private destroyed$ = new Subject<void>();

  areBlueprintsLoading: boolean = false;
  areItemSetsLoading: boolean = false;
  examTypeNames: { id: string, name: string }[] = [];
  risrAssessConnections: { id: string, name: string }[] = [];
  risrAssessBlueprints: { id: string, name: string }[] = [];
  risrAssessItemSets: { id: string, name: string }[] = [];

  constructor(private editorData: EditorDataService) {

  }

  ngOnInit() {
    this.examTypeNames = EXAM_TYPES_OPTIONS;

    this.initializeRisrAssessConnections();
    this.initFormValueListeners();
  }

  private initializeRisrAssessConnections(): void {
    this.editorData.risrAssessConnections$
      .pipe(takeUntil(this.destroyed$))
      .subscribe(connections => (this.risrAssessConnections = connections));
  }

  private initFormValueListeners(): void {
    if (!this.parentFormGroup) {
      console.warn('Parent form group is not initialized.');
      return;
    }

    this.watchFormControl('examType', this.handleExamTypeChange.bind(this), true);
    this.watchFormControl('risrAssessConnectionId', (connectionId) => this.fetchBlueprints(connectionId));
    this.watchFormControl('risrAssessBlueprintId', (blueprintId) => this.fetchItemSets(blueprintId));
  }

  private watchFormControl(
    controlName: string,
    callback: (value: any) => void,
    skipFirst: boolean = false
  ): void {
    const control = this.parentFormGroup.controls[controlName];
    if (!control) {
      console.warn(`Control ${controlName} is missing or not properly initialized.`);
      return;
    }

    let isInitialized = false;
    control.valueChanges
      .pipe(
        startWith(control.value),
        distinctUntilChanged(),
        tap(() => {
          if (skipFirst && !isInitialized) return;
          callback(control.value);
        }),
        takeUntil(this.destroyed$)
      )
      .subscribe({
        next: () => (isInitialized = true),
        error: (err) => console.error(`Error in ${controlName} control valueChanges`, err),
      });
  }

  private handleExamTypeChange(): void {
    console.log('ExamType Changed:', this.parentFormGroup.controls.examType.value);

    // Reset dependent fields
    this.resetFormControls({
      connectionId: true,
      blueprintId: true,
      generationMethod: true,
      itemSetNumber: true,
    });

    // Refetch blueprints using the existing fetchBlueprints logic
    const connectionId = this.parentFormGroup.controls.risrAssessConnectionId.value;
    this.fetchBlueprints(connectionId);
  }

  private fetchBlueprints(connectionId: string): void {
    this.resetBlueprints();
    if (!connectionId) return;

    this.areBlueprintsLoading = true;
    const examType: ExamTypeEnum = this.parentFormGroup.controls['examType']?.value;

    this.editorData.getRisrAssessBlueprints(connectionId, examType)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (blueprints) => {
          this.risrAssessBlueprints = blueprints;
          this.areBlueprintsLoading = false;
        },
        error: () => (this.areBlueprintsLoading = false),
      });
  }

  private fetchItemSets(blueprintId: string): void {
    this.resetItemSets();
    if (!blueprintId) return;

    this.areItemSetsLoading = true;
    const connectionId = this.parentFormGroup.controls['risrAssessConnectionId']?.value;

    this.editorData.getRisrAssessItemSets(connectionId, blueprintId)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (itemSets) => {
          this.risrAssessItemSets = itemSets;
          this.areItemSetsLoading = false;
        },
        error: () => (this.areItemSetsLoading = false),
      });
  }

  private resetBlueprints(): void {
    this.risrAssessBlueprints = [];
    this.areBlueprintsLoading = false;
  }

  private resetItemSets(): void {
    this.risrAssessItemSets = [];
    this.areItemSetsLoading = false;
  }


  isDeletable() {
    return this.itemTreeExamTypeForm.value !== null;
  }

  onDelete(): boolean {
    this.itemTreeExamTypeForm.markAsDirty();
    this.itemTreeExamTypeForm.setValue(null);

    this.resetFormControls({
      connectionId: true,
      blueprintId: true,
      generationMethod: true,
      itemSetNumber: true,
    });

    if (this.parentFormGroup?.controls?.examType) {
      this.parentFormGroup.controls.examType.setValue(null);
      this.parentFormGroup.controls.examType.markAsDirty();
      this.parentFormGroup.controls.examType.reset();
    }

    return false;
  }

  private resetFormControls(options: {
  connectionId: boolean;
  blueprintId: boolean;
  generationMethod: boolean;
  itemSetNumber: boolean;
}): void {
  const controls = this.parentFormGroup?.controls;

  if (!controls) return;

  // Map options keys to control names
  const controlMap = {
    connectionId: 'risrAssessConnectionId',
    blueprintId: 'risrAssessBlueprintId',
    generationMethod: 'risrAssessItemSetGenerationMethod',
    itemSetNumber: 'risrAssessItemSetNumber',
  };

  // Loop through the options and process each enabled control
  Object.entries(options).forEach(([key, shouldReset]) => {
    if (shouldReset) {
      const controlName = controlMap[key as keyof typeof controlMap]; // Get the control name
      const control = controls[controlName]; // Access the control

      if (control) {
        control.setValue(null, { emitEvent: false });
        control.markAsDirty();
        control.clearValidators(); // Clears all validators
        control.updateValueAndValidity();
      }
    }
  });
}


  isRisrAssessExamType() {
    const examType = this.parentFormGroup.get('examType')?.value;
    return !!examType;
  }

  isLoftExamType() {
    const examType = this.parentFormGroup.get('examType')?.value;
    return examType === ExamTypeEnum.LOFT_WRITTEN || examType === ExamTypeEnum.LOFT_OSCE;
  }

  isLoftOsceSelected() {
    const examType = this.parentFormGroup.get('examType')?.value;
    return examType === ExamTypeEnum.LOFT_OSCE;
  }

  isRisrConnectionSelected() {
    const connectionId = this.parentFormGroup.get('risrAssessConnectionId')?.value;
    return !!connectionId;
  }

  isRisrAssessItemSetGenerationMethodSelected() {
    return this.parentFormGroup.get('risrAssessItemSetGenerationMethod')?.value !== null;
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }


}
